/**
 * The type ladders every text control in this product steps along — the weights, the sizes, and the
 * snap that turns an arbitrary size into a stop on the ladder.
 *
 * A LEAF, and that is the whole reason the file exists. These three lived in `TextPropertiesPanel.tsx`,
 * which is a 900-line React component importing `FontPicker`, `CreditSlider`, the DeckGen editor
 * contexts and half of `sections/` — so anything that wanted the numbers dragged all of that behind it.
 * That was survivable while the only consumers were the canvas's own panels, which are downstream of
 * that graph anyway. It stopped being survivable when `v1_5/Preview`'s text toolbar wanted them: that
 * barrel is imported by the deck workspace and by every Preview story, and none of them has any
 * business loading a credit slider.
 *
 * `TextPropertiesPanel` re-exports all three, so nothing that imported them from there had to change.
 *
 * The values are unchanged and must stay that way. They are the reason a size set on the canvas, a size
 * set in the artifact editor and a size set from the preview's text toolbar all land on the same stop —
 * three surfaces editing one document, agreeing about what the sizes ARE.
 */

/** The six weights the product offers, by CSS value. Names are the ones the panels print. */
export const WEIGHT_OPTIONS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
]

// Stepped type-scale for the font-size slider. Includes every role preset (Caption 16 / Body 32 /
// H2 84 / H1 128 / Display 188 — see utils/typography) plus the 8/400 bounds, so picking a style
// lands the pill exactly on a stop. Off-scale sizes snap to the nearest stop.
export const FONT_SIZE_SCALE = [8, 12, 16, 24, 32, 48, 64, 84, 100, 128, 160, 188, 240, 320, 400]

/**
 * The CSS generic families, which are KEYWORDS and must never be quoted.
 *
 * Quoting one changes what it means: `font-family: "sans-serif"` asks for a font whose name is
 * literally "sans-serif", finds none, and falls through to the initial value.
 */
const CSS_GENERIC_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
])

/**
 * A family NAME as a `font-family` VALUE, safe to hand to `setProperty`.
 *
 * THIS EXISTS BECAUSE THE CSSOM SILENTLY REFUSES THE UNQUOTED FORM. An unquoted family is a sequence
 * of CSS identifiers, and an identifier may not begin with a digit — so `setProperty('font-family',
 * 'Font Awesome 5 Free')` and `'04b03'` are both rejected, the declaration is simply not written, and
 * nothing anywhere reports it. Measured, 2026-09-06: picking such a family from the preview's text
 * toolbar did nothing at all, twice, with no error. Multi-word names without a digit (`Playfair
 * Display`) happen to work unquoted, which is exactly why the bug is easy to ship.
 *
 * So everything is quoted except the generics, which must not be — see above. Quoting is harmless for
 * the names that already worked: the CSSOM serialises `Playfair Display` back as `"Playfair Display"`
 * either way.
 *
 * A name carrying a double quote or a backslash is escaped rather than dropped. Nothing in the product
 * generates one, but the families this is fed come from a model-authored document, and a value that
 * closed the string early would write an arbitrary declaration into someone's artifact.
 */
export function cssFontFamily(name: string): string {
  const trimmed = (name ?? '').trim()
  if (trimmed === '') return ''
  if (CSS_GENERIC_FAMILIES.has(trimmed.toLowerCase())) return trimmed
  // Already quoted by the caller: hand it back rather than quoting it twice.
  if (/^".*"$/.test(trimmed) || /^'.*'$/.test(trimmed)) return trimmed
  return `"${trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

/**
 * The stop nearest a live px value.
 *
 * Artifact text is authored freely — a document can carry any size at all — so a control cannot refuse
 * an off-scale value; it has to show the nearest stop and step from there.
 */
export const nearestFontSizeIndex = (size: number): number => {
  let best = 0
  for (let i = 1; i < FONT_SIZE_SCALE.length; i++) {
    if (Math.abs(FONT_SIZE_SCALE[i] - size) < Math.abs(FONT_SIZE_SCALE[best] - size)) best = i
  }
  return best
}
